import { Component, type ErrorInfo, type ReactNode } from 'react';
import { getStrings } from '@veylme/shared';

import { detectLocale } from '../i18n/detectLocale.js';
import { FallbackScreen } from './FallbackScreen.js';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Английский fallback — если даже getStrings/detectLocale подведут (не должны,
 * обе pure/try-catch-обёрнуты, но фолбэк — последняя линия обороны и не имеет
 * права сам упасть). Текст переиспользует существующий ключ `auth_error_*`
 * (packages/shared/src/i18n/*.ts, есть во всех 27 бандлах) — по смыслу
 * идентичен нужному «не удалось открыть — откройте заново внутри Telegram»,
 * даёт локализацию бесплатно, без правки shared-пакета (задача — только
 * apps/miniapp).
 */
const FALLBACK_TITLE = 'Access error';
const FALLBACK_DESC =
  'Failed to authenticate. Please close and reopen the Mini App from Telegram.';

/**
 * Класс, а не функция — React error boundaries возможны ТОЛЬКО через
 * classComponent (getDerivedStateFromError/componentDidCatch, нет хука-эквивалента).
 *
 * Ловит любую ошибку рендера в поддереве `children` (Веха аудита P2.1/MF-1):
 * без него `useLaunchParams()` в App.tsx (бросает вне Telegram/без launch params)
 * ронял всё дерево → белый экран без выхода. Оборачивается СНАРУЖИ `<App/>`
 * в main.tsx, поэтому ловит падения на любом уровне, включая сам App.tsx.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    // Критично: React error boundary ПЕРЕХВАТЫВАЕТ ошибку в componentDidCatch —
    // она НЕ долетает до window 'error' сама по себе. А armWatch.ts (mini-watch)
    // слушает именно глобальные window-события 'error'/'unhandledrejection'
    // (см. boot/watch.test.ts, кейс 1 — ErrorEvent с полями error/message).
    // Без явного ре-репорта падение осталось бы невидимым в ловушке.
    try {
      const err = error instanceof Error ? error : new Error(String(error));
      window.dispatchEvent(
        new ErrorEvent('error', { error: err, message: err.message }),
      );
    } catch {
      // dispatchEvent/ErrorEvent недоступны в окружении — фолбэк-экран уже
      // показан (state обновлён в getDerivedStateFromError), это не критично.
    }
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    let title: string = FALLBACK_TITLE;
    let desc: string = FALLBACK_DESC;
    try {
      // Прямой offline-доступ к строкам (НЕ useT()/useLocale()): хук берёт язык
      // из LocaleContext, а этот фолбэк рендерится СНАРУЖИ App.tsx, где
      // LocaleProvider мог даже не успеть смонтироваться (падение случилось
      // до него). detectLocale() сам try/catch-обёрнут (SDK может быть не
      // инициализирован) — но на случай будущих правок держим ещё один слой.
      const strings = getStrings(detectLocale());
      title = strings.auth_error_title;
      desc = strings.auth_error_desc;
    } catch {
      // Остаёмся на английском fallback выше — надёжность важнее локализации.
    }

    return <FallbackScreen title={title} desc={desc} />;
  }
}
